import { useCallback, useState } from 'react';
import { FlatList, Image, RefreshControl, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { FlaggedPhoto, listFlaggedPhotos } from '../api/admin';
import { Card, EmptyState, ErrorText, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { resolvePhotoUrl } from '../config';
import { ApiError } from '../api/client';

export default function AdminDuplicatePhotosScreen() {
  const { token } = useAuth();
  const [photos, setPhotos] = useState<FlaggedPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setPhotos(await listFlaggedPhotos(token));
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được danh sách ảnh, vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  return (
    <ScreenContainer>
      {loadError ? <ErrorText>{loadError}</ErrorText> : null}
      <FlatList
        data={photos}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={load} />}
        ListEmptyComponent={!loading && !loadError ? <EmptyState text="Không có ảnh nào bị nghi trùng lặp" /> : null}
        renderItem={({ item }) => (
          <Card>
            <Text style={{ fontSize: 14, fontFamily: fonts.bold, color: colors.text }}>
              {item.submission?.task?.title ?? 'Nhiệm vụ'}
            </Text>
            <Text style={{ color: colors.textMuted, marginTop: 2, fontSize: 12 }}>
              Shop: {item.submission?.task?.shop?.name ?? '—'}
            </Text>

            <View style={{ flexDirection: 'row', gap: 12, marginTop: 10 }}>
              <View style={{ alignItems: 'center' }}>
                <Image source={{ uri: resolvePhotoUrl(item.url) }} style={{ width: 130, height: 130, borderRadius: 8 }} />
                <Text style={{ color: colors.text, fontSize: 12, marginTop: 4, fontFamily: fonts.semiBold }}>
                  {item.submission?.reviewer?.name ?? '—'}
                </Text>
                <Text style={{ color: colors.textMuted, fontSize: 11 }}>
                  {item.submission?.reviewer?.email ?? '—'}
                </Text>
              </View>
              <Text style={{ color: colors.warning, alignSelf: 'center', fontSize: 20 }}>≈</Text>
              {item.original ? (
                <View style={{ alignItems: 'center' }}>
                  <Image
                    source={{ uri: resolvePhotoUrl(item.original.url) }}
                    style={{ width: 130, height: 130, borderRadius: 8 }}
                  />
                  <Text style={{ color: colors.text, fontSize: 12, marginTop: 4, fontFamily: fonts.semiBold }}>
                    {item.original.submission?.reviewer?.name ?? '—'}
                  </Text>
                  <Text style={{ color: colors.textMuted, fontSize: 11 }}>
                    {item.original.submission?.reviewer?.email ?? '—'}
                  </Text>
                </View>
              ) : (
                <Text style={{ color: colors.textMuted, alignSelf: 'center' }}>Ảnh gốc không còn</Text>
              )}
            </View>

            <Text style={{ color: colors.textMuted, marginTop: 8, fontSize: 11 }}>
              Nộp lúc: {new Date(item.createdAt).toLocaleString('vi-VN')}
            </Text>
          </Card>
        )}
      />
    </ScreenContainer>
  );
}
